import { useLocation } from 'react-router-dom'
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary'
import { logger } from '@clofo/lib/logger'
import { useTranslation } from '@clofo/i18n'
import { CircleAlertIcon } from 'lucide-react'

/**
 * It will catch uncaught errors thrown in the app or individual routes and display a fallback UI.
 * In development mode, it will display a pretty error overlay.
 * In production mode, it will display a simple error message.
 * This component should be used at the top level in the router config.
 * 
 * For example:
 * ```tsx
const router = createHashRouter([{
    element: (
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    ),
    children: [
      // ...your routes go here
    ]
  }])
 */
export function ErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation()

  return (
    <ReactErrorBoundary
      resetKeys={[location.pathname]}
      onError={error => {
        logger.error(error)
      }}
      FallbackComponent={ErrorFallback}
    >
      {children}
    </ReactErrorBoundary>
  )
}

function ErrorFallback({ error }: { error: unknown; resetErrorBoundary: () => void }) {
  const { t } = useTranslation()

  if (import.meta.env.DEV) {
    const err = error instanceof Error ? error : new Error(String(error))
    const stackLines = err.stack?.split('\n') || []
    const title = stackLines[0]
    const stackLinesData = stackLines.slice(1).map(line => {
      const lineNumberWithTimestamp = line.substring(line.indexOf('t=') + 2, line.indexOf(')'))
      const lineNumber = lineNumberWithTimestamp.substring(lineNumberWithTimestamp.indexOf(':') + 1)

      return {
        method: line.substring(line.indexOf('at ') + 3, line.indexOf('(')),
        filePath: line.substring(line.indexOf('Documents/') + 10, line.indexOf('t=') - 1),
        lineNumber,
      }
    })
    return (
      <div className='bg-background min-h-dvh p-6'>
        <div className='bg-card mx-auto max-w-4xl p-6 flex flex-col gap-4 border-1 rounded-xl'>
          <h1 className='text-sm text-destructive bg-destructive-light rounded-lg w-fit px-2 py-1'>
            Unhandled Error
          </h1>
          <h2 className='rounded-lg text-lg text-destructive px-1'>{title}</h2>
          <h3 className='font-medium px-1 text-lg'>Call Stack</h3>
          <ul className='px-2 flex flex-col gap-4'>
            {stackLinesData.map(line => (
              <li key={line.filePath}>
                <div className='font-semibold'>{line.method}</div>
                <div className='text-muted-foreground'>
                  {line.filePath} ({line.lineNumber})
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    )
  }

  return (
    <div className='flex items-center justify-center space-x-4 text-sm min-h-dvh'>
      <div className='text-destructive flex items-center gap-2'>
        <CircleAlertIcon />
        {t('errors.oops')}
      </div>
    </div>
  )
}
